import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  type QuestionAnswerHistoryEvent,
  getQuestionAnswerHistory,
  questionAnswerHistoryActorLabel,
} from "@/hera-api/question-answer-history-api";
import { formatAnswerValue, formatDateTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

function historyValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "Not answered";
  }
  if (typeof value === "string") {
    try {
      return formatAnswerValue(JSON.parse(value));
    } catch {
      return value;
    }
  }
  return formatAnswerValue(value);
}

interface HistoryEventGroup {
  key: string;
  changes: QuestionAnswerHistoryEvent[];
}

function saveKey(event: QuestionAnswerHistoryEvent): string {
  return [
    event.changed_at,
    event.actor_id ?? questionAnswerHistoryActorLabel(event),
    event.action ?? "",
  ].join("|");
}

function groupChangesBySave(
  events: readonly QuestionAnswerHistoryEvent[],
): HistoryEventGroup[] {
  const groups: HistoryEventGroup[] = [];
  const groupsByKey = new Map<string, HistoryEventGroup>();
  for (const event of events) {
    const key = saveKey(event);
    const existing = groupsByKey.get(key);
    if (existing) {
      existing.changes.push(event);
      continue;
    }
    const group: HistoryEventGroup = { key, changes: [event] };
    groupsByKey.set(key, group);
    groups.push(group);
  }
  return groups;
}

function HistoryChange({ event }: { event: QuestionAnswerHistoryEvent }) {
  return (
    <div className="space-y-1 py-2 first:pt-0 last:pb-0">
      <p className="text-sm font-medium">
        {event.question ?? `Question ${event.question_id}`}
      </p>
      <dl className="grid gap-2 text-sm sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start">
        <div className="min-w-0">
          <dt className="font-medium">Previous</dt>
          <dd className="whitespace-pre-wrap break-words text-muted-foreground">
            {historyValue(event.old_value)}
          </dd>
        </div>
        <span className="hidden pt-5 text-muted-foreground sm:block">→</span>
        <div className="min-w-0">
          <dt className="font-medium">New</dt>
          <dd className="whitespace-pre-wrap break-words text-muted-foreground">
            {historyValue(event.new_value)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function HistorySave({ group }: { group: HistoryEventGroup }) {
  const [first] = group.changes;
  return (
    <article className="space-y-2 rounded-md border p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
        <span className="text-sm font-medium">
          {formatDateTime(first.changed_at)}
        </span>
        {group.changes.length > 1 ? (
          <span className="text-sm text-muted-foreground">
            {group.changes.length} answers changed together
          </span>
        ) : null}
      </div>
      <p className="text-sm">
        <span className="text-muted-foreground">Changed by </span>
        {questionAnswerHistoryActorLabel(first)}
        {first.actor_user_type ? (
          <span className="text-muted-foreground">
            {" "}
            ({first.actor_user_type.toLowerCase()})
          </span>
        ) : null}
      </p>
      <div className="divide-y">
        {group.changes.map((event) => (
          <HistoryChange
            key={`${event.event_id}-${event.question_id}`}
            event={event}
          />
        ))}
      </div>
    </article>
  );
}

interface QuestionAnswerHistoryDialogProps {
  licenseId: string;
  questionIds: readonly string[];
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  elevated?: boolean;
}

export function QuestionAnswerHistoryDialog({
  licenseId,
  questionIds,
  title,
  open,
  onOpenChange,
  elevated = false,
}: QuestionAnswerHistoryDialogProps) {
  const historyQuery = useQuery({
    queryKey: ["getQuestionAnswerHistory", licenseId],
    queryFn: () => getQuestionAnswerHistory(licenseId),
    enabled: open && Boolean(licenseId),
  });

  const saves = useMemo(() => {
    const includedQuestionIds = new Set(questionIds);
    // One save writes every answer it touched at the same instant under the same
    // actor, so those changes belong on a single tile.
    return groupChangesBySave(
      historyQuery.data?.events.filter((event) =>
        includedQuestionIds.has(event.question_id),
      ) ?? [],
    );
  }, [historyQuery.data?.events, questionIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent elevated={elevated} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Previous and new answers, when they changed, and who changed them.
          </DialogDescription>
        </DialogHeader>
        {historyQuery.data?.truncated ? (
          <p className="text-sm text-muted-foreground">
            Only the most recent answer changes are available.
          </p>
        ) : null}
        {historyQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading history…</p>
        ) : historyQuery.isError ? (
          <p className="text-sm text-destructive">
            {historyQuery.error instanceof Error
              ? historyQuery.error.message
              : "Failed to load question answer history"}
          </p>
        ) : saves.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No answer changes were found.
          </p>
        ) : (
          <ScrollArea className="max-h-[70vh] pr-3">
            <div className="space-y-3">
              {saves.map((group) => (
                <HistorySave key={group.key} group={group} />
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
